import React, { useCallback, useState } from 'react';

// i18n
import { useTranslation, withTranslation } from 'react-i18next';

// Routes
import { Route, Switch, useLocation, withRouter } from 'react-router-dom';

import styled, { css } from 'styled-components';

// redux
import { push } from '../../navigation';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';

// Components
import { Button, Dialog } from '@betareader/design-system';
import AppIcon from '../../components/AppIcon';

import {
  loggedIn,
  getUserProfile,
  updateUserProfile,
} from '../../modules/user';

import { saveBook } from '../../modules/book';

import DiscoverSearch from '../discover/discoverSearch';
import Browse from '../discover/browse';

import { toggleBrowseMenu } from '../../modules/app';
import { updateSearchTerm } from '../../modules/betaOpportunities';
import NotificationsMenuItem from '../notifications/notificationsMenuItem';
import UpgradeMenuItem from './upgradeMenuItem';
import UserDropdown from './UserDropdown';
import ShareBookMenuItem from './shareBookMenuItem';
import { appUtil, authUtil } from '../../utils';
import EditBookContentContextMenu from './editBookContentContextMenu';
import BackMenuButton from './backMenuButton';
import SidebarToggler from './sidebarToggler';
import ChatMenuItem from './chatMenuItem';
import {useFeatureFlags} from '../../hooks/useFeatureFlags';

const discoveryReturnFor = bookId => {
  if (!bookId) return null;
  try {
    const saved = JSON.parse(sessionStorage.getItem('br-discovery-return'));
    return saved?.bookId === String(bookId) &&
      saved.path?.startsWith('/discover') &&
      Date.now() - saved.savedAt < 30 * 60 * 1000
      ? saved.path : null;
  } catch { return null; }
};

export const MenuWrapper = styled.nav.attrs({className: 'ui secondary menu'})`
  background: ${({ theme }) => theme.background.contextMenu} !important;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: var(--header-height);
  min-width: 0;
  width: 100%;
  margin: 0px !important;

  > .item {
    min-width: 0;
    padding: 0 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const HeaderWrapper = styled.header`
  z-index: 1;
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.background.contextMenu};
  box-shadow: 0px 2px 5px ${({ theme }) => theme.navigation.headerShadow};
  flex-direction: column;
  max-width: 100%;
`;

export const TitleMenuItemWrapper = styled.div.attrs({className: 'item header'})`
  flex: 1 2 300px;
  max-width: 40%;
`;

export const TitleWrapper = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RightMenuWrapper = styled.div.attrs({className: 'right menu'})`
  /* position: absolute; */
  /* right: 0px; */
  background: ${({ theme }) => theme.background.contextMenu} !important;
  align-items: center;
  display: flex;
  flex: none;
  margin-left: auto;
`;

const Spacer = styled.div`
  ${({ width }) => css`
    width: ${width}px;
  `}
`;

const FontSizeMenu = styled.details`
  position: relative;
  display: inline-flex;
  align-items: center;

  summary { list-style: none; cursor: pointer; padding: 0.65rem; }
  summary::-webkit-details-marker { display: none; }
  &[open] > div {
    position: absolute;
    right: 0;
    top: 100%;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem;
    background: var(--br-surface, white);
    border: 1px solid var(--br-border, #ccc);
    border-radius: 0.5rem;
    white-space: nowrap;
  }
`;

export const ContextMenu = ({
  fontSizes,
  userProfile,
  currentBook,
  saveButton,
  currentPartTitle,
  searchTerm,
  sidebarVisible,

  updateUserProfile,
}) => {
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const {flags} = useFeatureFlags();
  const signedInUser = useSelector(state => state.user.user);
  const experimentAssignment = useSelector(state => state.app.discoveryExperimentAssignment);
  const discoveryRedesign = flags?.discovery_redesign === true ||
    (flags?.discovery_redesign_beta_access_allowed === true &&
      userProfile?.betaFeatures?.discovery_redesign === true);
  const flagPhase = flags?.discovery_experiment === true &&
    flags?.discovery_experiment_aa !== true ? 'ab' :
    flags?.discovery_experiment_aa === true && flags?.discovery_experiment !== true ? 'aa' : null;
  const experimentEligible = Boolean(flagPhase) && !discoveryRedesign &&
    !signedInUser?.isAnonymous &&
    Boolean(userProfile?._id && userProfile._id === signedInUser?.uid);
  const experimentReady = !experimentEligible ||
    (experimentAssignment?.uid === userProfile?._id &&
      experimentAssignment?.requestedPhase === flagPhase);
  const showLegacyDiscoveryMenu = !discoveryRedesign && experimentReady &&
    (!experimentEligible || experimentAssignment?.phase !== 'ab' ||
      experimentAssignment?.variant !== 'treatment');
  const currentFontSizeIndex = useCallback(() => {
    if (!userProfile?.readerSettings) {
      return 0;
    }
    const currentFontSizeIndex = fontSizes.findIndex(
      size => size.name === userProfile?.readerSettings.fontSize
    );
    return currentFontSizeIndex;
  }, [fontSizes, userProfile?.readerSettings]);

  const updateFontSize = useCallback(
    async fontSize => {
      const idToken = await authUtil.getFreshIdToken();
      updateUserProfile(idToken, {
        readerSettings: {
          ...userProfile.readerSettings,
          fontSize,
        },
      });
    },
    [updateUserProfile, userProfile?.readerSettings]
  );

  const increaseFontSize = useCallback(() => {
    const nextFontSizeIndex = currentFontSizeIndex() + 1;
    if (nextFontSizeIndex >= 0 && nextFontSizeIndex < fontSizes.length) {
      updateFontSize(fontSizes[nextFontSizeIndex].name);
    }
  }, [currentFontSizeIndex, updateFontSize, fontSizes]);

  const decreaseFontSize = useCallback(() => {
    const nextFontSizeIndex = currentFontSizeIndex() - 1;
    if (nextFontSizeIndex >= 0 && nextFontSizeIndex < fontSizes.length) {
      updateFontSize(fontSizes[nextFontSizeIndex].name);
    }
  }, [currentFontSizeIndex, updateFontSize, fontSizes]);

  const getUpgradeAndNotificationMenuItems = useCallback(() => {
    if (!userProfile) {
      return null;
    }
    return [
      !appUtil.isSmallScreen() && <ShareBookMenuItem key="share" />,
      !appUtil.isSmallScreen() && <UpgradeMenuItem key="upgrade" />,
      <ChatMenuItem key="chat" />,
      location.pathname !== '/read' && (
        <NotificationsMenuItem key="notifications" fitted link />
      ),
      location?.pathname === '/read' && [
        currentBook && (
          <React.Fragment key="feedbackGuidelines">
            <Button
              variant="ghost"
              aria-label={t('FeedbackGuidelines')}
              onClick={() => setGuidelinesOpen(true)}
            >
              <span aria-hidden="true">☰</span>
            </Button>
            <Dialog
              open={guidelinesOpen}
              title={t('FeedbackGuidelines')}
              onClose={() => setGuidelinesOpen(false)}
              actions={<Button variant="secondary" onClick={() => setGuidelinesOpen(false)}>{t('Close')}</Button>}
            >
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {currentBook.feedbackGuidelines || t('NoGuidelines')}
              </div>
            </Dialog>
          </React.Fragment>
        ),
        <FontSizeMenu key="font">
          <summary aria-label={t('FontSize', { defaultValue: 'Font size' })}><span aria-hidden="true">A</span></summary>
          <div>
            <Button
              variant="ghost"
              size="small"
              aria-label={t('DecreaseFontSize', { defaultValue: 'Decrease font size' })}
              onClick={decreaseFontSize}
              disabled={!userProfile || userProfile.readerSettings?.fontSize === 'small'}
            >−</Button>
              <span aria-hidden="true">A</span>
            <Button
              variant="ghost"
              size="small"
              aria-label={t('IncreaseFontSize', { defaultValue: 'Increase font size' })}
              onClick={increaseFontSize}
              disabled={!userProfile || userProfile.readerSettings?.fontSize === 'big'}
            >+</Button>
          </div>
        </FontSizeMenu>,
      ],
    ];
  }, [
    currentBook,
    increaseFontSize,
    decreaseFontSize,
    location.pathname,
    guidelinesOpen,
    t,
    userProfile,
  ]);

  const getCurrentContextMenu = useCallback(() => {
    const { pathname } = location;
    const menuBar = [];
    if (pathname.includes('/exit-survey')) {
      menuBar.push(
        <div className="item" key="title">{t('LeaveBeta')}</div>
      );
    }
    if (pathname === '/feed') {
      menuBar.push(
        <div className="item" key="feed">{t('Feed')}</div>
      );
    }
    if (pathname.includes('/mymanuscripts')) {
      menuBar.push(
        <div className="item" key="title">{t('MyManuscripts')}</div>
      );
    } else if (pathname.includes('/account')) {
      menuBar.push(
        <div className="item" key="title">{t('Account')}</div>
      );
    } else if (pathname.includes('/inbox')) {
      menuBar.push(
        <div className="item" key="title">{t('Chat')}</div>
      );
    } else if (pathname.includes('/manuscript-inbox/submission')) {
      menuBar.push(
        <div className="item" key="title">{t('SubmitManuscript')}</div>
      );
    } else if (pathname.includes('/manuscript-inbox')) {
      menuBar.push(
        <div className="item" key="title">{t('ManuscriptInboxes')}</div>
      );
    } else if (pathname.includes('/discover')) {
      if (showLegacyDiscoveryMenu) menuBar.push(
        <Browse key="browse" />,
        <DiscoverSearch key="search" />
      );
    } else if (pathname.includes('/readinglist')) {
      menuBar.push(
        <div className="item" key="title">{t('ReadingList')}</div>
      );
    } else if (pathname.includes('/notifications')) {
      menuBar.push(
        <div className="item" key="title">{t('Notifications')}</div>
      );
    } else if (pathname.startsWith('/readers')) {
      menuBar.push(<div className="item" key="title">{t('ReaderList')}</div>);
    } else if (pathname === '/read' && currentBook) {
      menuBar.push(
        ...[
          <TitleMenuItemWrapper
            key="title"
          >
              <TitleWrapper>
                <AppIcon name="book" />
                <span>{currentBook.title}</span>
              </TitleWrapper>
          </TitleMenuItemWrapper>,
        ]
      );
    } else if (pathname.startsWith('/books') && currentBook) {
      menuBar.push(
        ...[
          <TitleMenuItemWrapper
            key="title"
          >
              <TitleWrapper>
                <AppIcon name="book" />
                <span>{currentPartTitle || currentBook.title}</span>
              </TitleWrapper>
          </TitleMenuItemWrapper>,
          saveButton && (
            <div className="item" key="save-book" style={{marginLeft: 'auto'}}>{saveButton}</div>
          ),
        ]
      );
    }
    menuBar.push(
      <RightMenuWrapper key="right-menu">
        {getUpgradeAndNotificationMenuItems()}
        <UserDropdown />
      </RightMenuWrapper>
    );
    return menuBar;
  }, [
    location,
    searchTerm,
    saveButton,
    currentBook,
    currentPartTitle,
    getUpgradeAndNotificationMenuItems,
    t,
    showLegacyDiscoveryMenu,
  ]);

  // Show edit content menu
  const inContentMode = location.pathname.includes('/content');

  const getTopMenu = useCallback(
    () => (
      <MenuWrapper
        key="context-menu"
        aria-label={t('ContextMenu', 'Context menu')}
        $sidebarVisible={sidebarVisible}
      >
        <Switch>
          <Route path={'/read'}>
            {/* when reading, the back button should lead to the reading list */}
            <BackMenuButton toPath={'/readinglist'} />
          </Route>
          <Route path={'/books/:bookId/content'}>
            {/* when editing, the back button should lead to the book overview */}
            <BackMenuButton toPath={`/books/${currentBook?._id}/overview`} />
          </Route>
          <Route path={'/books/:bookId'}>
            {/* when in the other book "sub views", the back button should lead to /my-manuscripts
           for editors, and to discover for readers */}
            <BackMenuButton
              toPath={
                (discoveryRedesign && discoveryReturnFor(currentBook?._id)) ||
                (['author', 'collaborator'].includes(currentBook?.role)
                  ? '/mymanuscripts' : '/discover')
              }
            />
          </Route>
          <Route>
            <Spacer width={14} />
          </Route>
        </Switch>
        {inContentMode ? (
          <EditBookContentContextMenu />
        ) : (
          [<SidebarToggler key="sidebar-toggler" />, getCurrentContextMenu()]
        )}
      </MenuWrapper>
    ),
    [getCurrentContextMenu, sidebarVisible, inContentMode, currentBook]
  );

  return <HeaderWrapper>{getTopMenu()}</HeaderWrapper>;
};

// redux stuff
const mapStateToProps = state => ({
  user: state.user.user,
  idToken: state.user.idToken,
  userProfile: state.user.userProfile,
  currentBook: state.book.currentBook,
  currentPartTitle: state.book.currentPartTitle,
  currentSpineIndex: state.readerApp.currentSpineIndex,
  pendingBookChanges: state.book.pendingBookChanges,
  pendingPartChanges: state.book.pendingPartChanges,
  saveButton: state.book.saveButton,
  alerts: state.app.alerts,
  percentagePassed: state.readerApp.percentagePassed,
  sidebarVisible: state.app.sidebarVisible,
  fontSizes: state.app.fontSizes,
  searchTerm: state.betaOpportunities.searchTerm,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      loggedIn,
      getUserProfile,
      saveBook,
      toggleBrowseMenu,
      updateSearchTerm,
      updateUserProfile,
      changePage: newPage => push(newPage),
    },
    dispatch
  );

export default withTranslation()(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(ContextMenu))
);
